import * as React from 'react';
import { brandColors, companyLogo, companyConfig } from '../config/colors';

interface ClassConfirmationEmailV2Props {
  // Dados da sessão da aula
  class_session_id?: string;
  class_session_status?: string;
  start_date?: string;
  end_date?: string;
  duration_minutes?: number;
  teams_meeting_url?: string;
  custom_meeting_url?: string;
  meeting_url_type?: string;
  mode?: string; // presencial, online, híbrido
  
  // Dados da aula e curso
  class_id?: string;
  display_id?: string;
  course_id?: string;
  name?: string; // nome do curso
  family_name?: string;
  location_address?: string;
  
  // Dados do instrutor
  instructor_id?: string;
  instructor_name?: string;
  
  // Dados do participante
  email?: string;
  
  // Dados da organização
  org_name?: string;
  domain?: string;
  industry?: string;
  
  // Dados da assinatura
  subscription_id?: string;
  subscription_start_date?: string;
  subscription_end_date?: string;
  
  // Outros
  session_count?: number;
  created_at?: string;
  participant_name?: string;
}

export const ClassConfirmationEmailV2: React.FC<ClassConfirmationEmailV2Props> = ({
  class_session_id = 'CS-789012',
  class_session_status = 'confirmed',
  start_date = '2025-04-15T09:00:00Z',
  end_date = '2025-04-15T17:00:00Z',
  duration_minutes = 480,
  teams_meeting_url = 'https://teams.microsoft.com/l/meetup-join/...',
  custom_meeting_url = '',
  meeting_url_type = 'teams',
  mode = 'hybrid',
  
  class_id = 'CLS-456',
  display_id = 'FA-2025-001',
  course_id = 'COURSE-123',
  name = 'Fundamentos de Data Analytics',
  family_name = 'Analytics Essentials',
  location_address = 'Av. Paulista, 1000 - Sala 203, São Paulo - SP',
  
  instructor_id = 'INST-001',
  instructor_name = 'Dra. Ana Carolina Soares',
  
  email = 'joao.silva@empresa.com.br',
  participant_name = 'João Silva',
  
  org_name = 'Empresa XYZ Ltda',
  domain = 'empresaxyz.com.br',
  industry = 'Tecnologia',
  
  subscription_id = 'SUB-789',
  subscription_start_date = '2025-01-01',
  subscription_end_date = '2025-12-31',
  
  session_count = 1,
  created_at = '2025-01-15T10:30:00Z',
}) => {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return mins > 0 ? `${hours}h ${mins}min` : `${hours}h`;
    }
    return `${mins}min`;
  };

  const getModeIcon = (mode: string) => {
    switch (mode.toLowerCase()) {
      case 'online':
        return '💻';
      case 'presencial':
        return '🏢';
      case 'hybrid':
      default:
        return '🔄';
    }
  };

  const getModeText = (mode: string) => {
    switch (mode.toLowerCase()) {
      case 'online':
        return 'Online';
      case 'presencial':
        return 'Presencial';
      case 'hybrid':
      default:
        return 'Híbrido';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'confirmed':
        return brandColors.success;
      case 'pending':
        return brandColors.warning;
      case 'cancelled':
        return '#ef4444';
      default:
        return brandColors.primary;
    }
  };

  const getStatusText = (status: string) => {
    switch (status.toLowerCase()) {
      case 'confirmed':
        return 'Confirmada';
      case 'pending':
        return 'Pendente';
      case 'cancelled':
        return 'Cancelada';
      default:
        return 'Confirmada';
    }
  };

  const meetingUrl = custom_meeting_url || teams_meeting_url;

  return (
    <div style={containerStyle}>
      {/* Header */}
      <div style={headerStyle}>
        <img 
          src={companyLogo}
          alt={companyConfig.name}
          style={logoImageStyle}
        />
      </div>
      
      {/* Hero Section */}
      <div style={heroSectionStyle}>
        <div style={heroContentStyle}>
          {/* Status Badge */}
          <div style={{
            ...statusBadgeStyle,
            backgroundColor: getStatusColor(class_session_status),
          }}>
            <span style={statusIconStyle}>✓</span>
            <span>{getStatusText(class_session_status)}</span>
          </div>
          
          <h1 style={heroTitleStyle}>
            Sua aula foi confirmada!
          </h1>
          
          <p style={heroSubtitleStyle}>
            Olá {participant_name}, sua participação na aula foi registrada com sucesso.
          </p>
          
          <div style={sessionIdStyle}>
            Sessão #{class_session_id}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div style={mainContentStyle}>
        
        {/* Course Card */}
        <div style={courseCardStyleV2}>
          <div style={courseHeaderStyleV2}>
            <div style={courseHeaderLeftStyle}>
              <h2 style={courseTitleStyleV2}>{name}</h2>
              <div style={courseFamilyStyle}>{family_name}</div>
            </div>
            <div style={courseHeaderRightStyle}>
              <div style={modeIndicatorStyle}>
                <span style={modeIconStyle}>{getModeIcon(mode)}</span>
                <span style={modeTextStyle}>{getModeText(mode)}</span>
              </div>
            </div>
          </div>
          
          <div style={courseMetaStyle}>
            <div style={courseMetaItemStyle}>
              <span style={metaLabelStyle}>ID do Curso:</span>
              <span style={metaValueStyle}>{display_id}</span>
            </div>
            <div style={courseMetaItemStyle}>
              <span style={metaLabelStyle}>Duração:</span>
              <span style={metaValueStyle}>{formatDuration(duration_minutes)}</span>
            </div>
            <div style={courseMetaItemStyle}>
              <span style={metaLabelStyle}>Sessão:</span>
              <span style={metaValueStyle}>{session_count}ª aula</span>
            </div>
          </div>
        </div>

        {/* Date & Time Card */}
        <div style={infoCardStyle}>
          <div style={cardHeaderStyle}>
            <span style={cardIconStyle}>📅</span>
            <h3 style={cardTitleStyle}>Data e Horário</h3>
          </div>
          <div style={cardContentStyle}>
            <div style={dateTimeRowStyle}>
              <div style={dateTimeItemStyle}>
                <div style={dateTimeLabelStyle}>Data</div>
                <div style={dateTimeValueStyle}>{formatDate(start_date)}</div>
              </div>
              <div style={dateTimeItemStyle}>
                <div style={dateTimeLabelStyle}>Horário</div>
                <div style={dateTimeValueStyle}>
                  {formatTime(start_date)} às {formatTime(end_date)}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Instructor Card */}
        <div style={infoCardStyle}>
          <div style={cardHeaderStyle}>
            <span style={cardIconStyle}>👨‍🏫</span>
            <h3 style={cardTitleStyle}>Instrutor</h3>
          </div>
          <div style={cardContentStyle}>
            <div style={instructorInfoStyle}>
              <div style={instructorNameStyle}>{instructor_name}</div>
              <div style={instructorIdStyle}>ID: {instructor_id}</div>
            </div>
          </div>
        </div>

        {/* Location/Meeting Card */}
        <div style={infoCardStyle}>
          <div style={cardHeaderStyle}>
            <span style={cardIconStyle}>
              {mode.toLowerCase() === 'online' ? '💻' : mode.toLowerCase() === 'presencial' ? '📍' : '🔄'}
            </span>
            <h3 style={cardTitleStyle}>
              {mode.toLowerCase() === 'online' ? 'Link da Reunião' : 
               mode.toLowerCase() === 'presencial' ? 'Local' : 'Local e Reunião'}
            </h3>
          </div>
          <div style={cardContentStyle}>
            {(mode.toLowerCase() === 'presencial' || mode.toLowerCase() === 'hybrid') && (
              <div style={locationInfoStyle}>
                <div style={locationLabelStyle}>Endereço:</div>
                <div style={locationValueStyle}>{location_address}</div>
              </div>
            )}
            
            {(mode.toLowerCase() === 'online' || mode.toLowerCase() === 'hybrid') && meetingUrl && (
              <div style={meetingInfoStyle}>
                <div style={meetingLabelStyle}>
                  {meeting_url_type === 'teams' ? 'Microsoft Teams' : 'Link da Reunião'}:
                </div>
                <a href={meetingUrl} style={meetingLinkStyle}>
                  Participar da Reunião
                </a>
              </div>
            )}
          </div>
        </div>

        {/* Organization Card */}
        <div style={infoCardStyle}>
          <div style={cardHeaderStyle}>
            <span style={cardIconStyle}>🏢</span>
            <h3 style={cardTitleStyle}>Organização</h3>
          </div>
          <div style={cardContentStyle}>
            <div style={orgInfoStyle}>
              <div style={orgNameStyle}>{org_name}</div>
              <div style={orgDetailsStyle}>
                <span>{industry}</span> • <span>{domain}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div style={actionButtonsStyle}>
          <a href="#" style={primaryButtonStyle}>
            📅 Adicionar ao Calendário
          </a>
          {meetingUrl && (
            <a href={meetingUrl} style={secondaryButtonStyle}>
              🔗 Acessar Reunião
            </a>
          )}
        </div>

        {/* Important Notes */}
        <div style={notesCardStyle}>
          <div style={notesHeaderStyle}>
            <span style={notesIconStyle}>💡</span>
            <h3 style={notesTitleStyle}>Informações Importantes</h3>
          </div>
          <div style={notesContentStyle}>
            <ul style={notesListStyle}>
              <li style={notesItemStyle}>
                Chegue com 15 minutos de antecedência para o credenciamento
              </li>
              <li style={notesItemStyle}>
                Traga um documento de identificação com foto
              </li>
              {mode.toLowerCase() !== 'presencial' && (
                <li style={notesItemStyle}>
                  Teste seu áudio e vídeo antes do início da aula
                </li>
              )}
              <li style={notesItemStyle}>
                Em caso de dúvidas, entre em contato conosco
              </li>
            </ul>
          </div>
        </div>

      </div>
      
      {/* Footer */}
      <div style={footerStyle}>
        <div style={footerContentStyle}>
          <p style={footerTextStyle}>
            Precisa de ajuda? Entre em contato conosco:
          </p>
          <div style={contactInfoStyle}>
            <span style={contactItemStyle}>✉️ {companyConfig.supportEmail}</span>
            <span style={contactItemStyle}>📱 {companyConfig.phone}</span>
          </div>
          <p style={footerSignatureStyle}>
            Atenciosamente,<br />
            Equipe {companyConfig.name}
          </p>
        </div>
        
        <hr style={footerDividerStyle} />
        
        <div style={footerBottomStyle}>
          <p style={copyrightStyle}>
            © 2025 {companyConfig.name}. Todos os direitos reservados.
          </p>
          <div style={footerLinksStyle}>
            <a href="#" style={footerLinkStyle}>Termos de Uso</a>
            <span style={footerSeparatorStyle}>|</span>
            <a href="#" style={footerLinkStyle}>Política de Privacidade</a>
          </div>
        </div>
      </div>
    </div>
  );
};

// Estilos
const containerStyle = {
  fontFamily: "'Satoshi', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  maxWidth: '650px',
  margin: '0 auto',
  backgroundColor: '#f8fafc',
  lineHeight: '1.6',
};

const headerStyle = {
  backgroundColor: brandColors.header,
  padding: '20px',
  textAlign: 'center' as const,
  borderTopLeftRadius: '12px',
  borderTopRightRadius: '12px',
};

const logoImageStyle = {
  maxWidth: '160px',
  height: 'auto',
  display: 'block',
  margin: '0 auto',
};

const heroSectionStyle = {
  background: `linear-gradient(135deg, ${brandColors.primary} 0%, ${brandColors.secondary} 100%)`,
  padding: '50px 30px',
  textAlign: 'center' as const,
  color: brandColors.white,
};

const heroContentStyle = {
  maxWidth: '500px',
  margin: '0 auto',
};

const statusBadgeStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '8px',
  padding: '8px 16px',
  borderRadius: '20px',
  fontSize: '14px',
  fontWeight: '600',
  color: brandColors.white,
  marginBottom: '20px',
};

const statusIconStyle = {
  fontSize: '16px',
};

const heroTitleStyle = {
  fontSize: '32px',
  fontWeight: '700',
  margin: '0 0 12px 0',
  color: brandColors.white,
};

const heroSubtitleStyle = {
  fontSize: '18px',
  margin: '0 0 25px 0',
  opacity: '0.9',
  color: brandColors.white,
};

const sessionIdStyle = {
  display: 'inline-block',
  padding: '10px 20px',
  backgroundColor: 'rgba(255, 255, 255, 0.2)',
  borderRadius: '25px',
  fontSize: '16px',
  fontWeight: '600',
  color: brandColors.white,
  backdropFilter: 'blur(10px)',
};

const mainContentStyle = {
  padding: '30px',
  backgroundColor: brandColors.white,
};

const courseCardStyleV2 = {
  backgroundColor: brandColors.white,
  border: `2px solid ${brandColors.primary}`,
  borderRadius: '16px',
  overflow: 'hidden',
  marginBottom: '24px',
  boxShadow: '0 8px 32px rgba(59, 101, 245, 0.15)',
};

const courseHeaderStyleV2 = {
  backgroundColor: brandColors.primary,
  padding: '24px',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
};

const courseHeaderLeftStyle = {
  flex: '1',
};

const courseHeaderRightStyle = {
  marginLeft: '20px',
};

const courseTitleStyleV2 = {
  margin: '0 0 8px 0',
  color: brandColors.white,
  fontSize: '22px',
  fontWeight: '700',
};

const courseFamilyStyle = {
  color: 'rgba(255, 255, 255, 0.8)',
  fontSize: '14px',
  fontWeight: '500',
};

const modeIndicatorStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  backgroundColor: 'rgba(255, 255, 255, 0.2)',
  padding: '8px 12px',
  borderRadius: '12px',
  backdropFilter: 'blur(10px)',
};

const modeIconStyle = {
  fontSize: '16px',
};

const modeTextStyle = {
  color: brandColors.white,
  fontSize: '14px',
  fontWeight: '600',
};

const courseMetaStyle = {
  padding: '20px 24px',
  display: 'flex',
  gap: '20px',
  flexWrap: 'wrap' as const,
};

const courseMetaItemStyle = {
  display: 'flex',
  flexDirection: 'column' as const,
  gap: '4px',
};

const metaLabelStyle = {
  fontSize: '12px',
  color: brandColors.secondaryText,
  fontWeight: '500',
  textTransform: 'uppercase' as const,
  letterSpacing: '0.5px',
};

const metaValueStyle = {
  fontSize: '14px',
  color: brandColors.text,
  fontWeight: '600',
};

const infoCardStyle = {
  backgroundColor: brandColors.white,
  border: `1px solid ${brandColors.border}`,
  borderRadius: '12px',
  marginBottom: '16px',
  overflow: 'hidden',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
};

const cardHeaderStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  padding: '16px 20px',
  backgroundColor: '#f8fafc',
  borderBottom: `1px solid ${brandColors.border}`,
};

const cardIconStyle = {
  fontSize: '20px',
};

const cardTitleStyle = {
  margin: '0',
  fontSize: '16px',
  fontWeight: '600',
  color: brandColors.text,
};

const cardContentStyle = {
  padding: '20px',
};

const dateTimeRowStyle = {
  display: 'flex',
  gap: '30px',
  flexWrap: 'wrap' as const,
};

const dateTimeItemStyle = {
  flex: '1',
  minWidth: '200px',
};

const dateTimeLabelStyle = {
  fontSize: '14px',
  color: brandColors.secondaryText,
  marginBottom: '4px',
  fontWeight: '500',
};

const dateTimeValueStyle = {
  fontSize: '16px',
  color: brandColors.text,
  fontWeight: '600',
};

const instructorInfoStyle = {
  display: 'flex',
  flexDirection: 'column' as const,
  gap: '4px',
};

const instructorNameStyle = {
  fontSize: '18px',
  fontWeight: '600',
  color: brandColors.text,
};

const instructorIdStyle = {
  fontSize: '14px',
  color: brandColors.secondaryText,
};

const locationInfoStyle = {
  marginBottom: '16px',
};

const locationLabelStyle = {
  fontSize: '14px',
  color: brandColors.secondaryText,
  marginBottom: '4px',
  fontWeight: '500',
};

const locationValueStyle = {
  fontSize: '16px',
  color: brandColors.text,
  fontWeight: '500',
};

const meetingInfoStyle = {
  display: 'flex',
  flexDirection: 'column' as const,
  gap: '8px',
};

const meetingLabelStyle = {
  fontSize: '14px',
  color: brandColors.secondaryText,
  fontWeight: '500',
};

const meetingLinkStyle = {
  display: 'inline-block',
  padding: '10px 16px',
  backgroundColor: brandColors.primary,
  color: brandColors.white,
  textDecoration: 'none',
  borderRadius: '8px',
  fontSize: '14px',
  fontWeight: '600',
  transition: 'all 0.2s ease',
};

const orgInfoStyle = {
  display: 'flex',
  flexDirection: 'column' as const,
  gap: '6px',
};

const orgNameStyle = {
  fontSize: '18px',
  fontWeight: '600',
  color: brandColors.text,
};

const orgDetailsStyle = {
  fontSize: '14px',
  color: brandColors.secondaryText,
};

const actionButtonsStyle = {
  display: 'flex',
  gap: '12px',
  margin: '30px 0',
  flexWrap: 'wrap' as const,
};

const primaryButtonStyle = {
  display: 'inline-block',
  padding: '14px 24px',
  backgroundColor: brandColors.primary,
  color: brandColors.white,
  textDecoration: 'none',
  borderRadius: '10px',
  fontSize: '16px',
  fontWeight: '600',
  transition: 'all 0.2s ease',
  flex: '1',
  textAlign: 'center' as const,
  minWidth: '200px',
};

const secondaryButtonStyle = {
  display: 'inline-block',
  padding: '14px 24px',
  backgroundColor: brandColors.white,
  color: brandColors.primary,
  textDecoration: 'none',
  border: `2px solid ${brandColors.primary}`,
  borderRadius: '10px',
  fontSize: '16px',
  fontWeight: '600',
  transition: 'all 0.2s ease',
  flex: '1',
  textAlign: 'center' as const,
  minWidth: '200px',
};

const notesCardStyle = {
  backgroundColor: '#fefce8',
  border: '1px solid #fde047',
  borderRadius: '12px',
  overflow: 'hidden',
  marginTop: '20px',
};

const notesHeaderStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  padding: '16px 20px',
  backgroundColor: '#fef3c7',
  borderBottom: '1px solid #fde047',
};

const notesIconStyle = {
  fontSize: '20px',
};

const notesTitleStyle = {
  margin: '0',
  fontSize: '16px',
  fontWeight: '600',
  color: '#92400e',
};

const notesContentStyle = {
  padding: '20px',
};

const notesListStyle = {
  margin: '0',
  paddingLeft: '20px',
  color: '#92400e',
};

const notesItemStyle = {
  marginBottom: '8px',
  fontSize: '14px',
  lineHeight: '1.5',
};

const footerStyle = {
  backgroundColor: brandColors.background,
  padding: '30px',
  borderBottomLeftRadius: '12px',
  borderBottomRightRadius: '12px',
};

const footerContentStyle = {
  textAlign: 'center' as const,
  marginBottom: '20px',
};

const footerTextStyle = {
  fontSize: '14px',
  color: brandColors.secondaryText,
  margin: '0 0 12px 0',
};

const contactInfoStyle = {
  display: 'flex',
  justifyContent: 'center',
  gap: '20px',
  flexWrap: 'wrap' as const,
  marginBottom: '16px',
};

const contactItemStyle = {
  fontSize: '14px',
  color: brandColors.text,
  fontWeight: '500',
};

const footerSignatureStyle = {
  fontSize: '14px',
  color: brandColors.secondaryText,
  margin: '0',
};

const footerDividerStyle = {
  border: 'none',
  borderTop: `1px solid ${brandColors.border}`,
  margin: '20px 0',
};

const footerBottomStyle = {
  textAlign: 'center' as const,
};

const copyrightStyle = {
  fontSize: '12px',
  color: brandColors.secondaryText,
  margin: '0 0 8px 0',
};

const footerLinksStyle = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  gap: '8px',
  flexWrap: 'wrap' as const,
};

const footerLinkStyle = {
  color: brandColors.primary,
  textDecoration: 'none',
  fontSize: '12px',
  fontWeight: '500',
};

const footerSeparatorStyle = {
  color: brandColors.secondaryText,
  fontSize: '12px',
};

export default ClassConfirmationEmailV2;

